import { Link } from '@tanstack/react-router';
import { AlertTriangle, Bot, RefreshCw, ShieldAlert } from 'lucide-react';
import { useState, type ReactNode } from 'react';
import { useTranslation } from 'react-i18next';
import type { TFunction } from 'i18next';
import type {
  AiReviewAgreementResponse,
  AiReviewAgreementRow,
  AiReviewDisagreementRow,
  ReviewCaseDecision,
  ShadowFairness,
  ShadowVerdict,
} from '@pf/contracts';
import { useAuth } from '@/auth/auth-context';
import {
  useAiReviewAgreement,
  useAiReviewDisagreements,
  type AiReviewReviewerSelection,
  type MetricsRange,
} from '@/lib/api/ops-queries';
import { formatDateTimeCompact, formatRelative } from '@/lib/datetime';
import { PAGE_SIZES } from '@/lib/pagination';
import { REVIEW_REASON_META, TONE_CLASSES, type StateTone } from '@/lib/states';
import { cn, shortId } from '@/lib/utils';
import { MoneyDisplay } from '@/components/domain/money-display';
import { DataTable, type Column } from '@/components/layout/data-table';
import { PageHeader, Section } from '@/components/layout/page';
import { Pager } from '@/components/layout/pager';
import { CustomRangePicker, windowButtonClasses } from '@/components/layout/range-picker';
import { RequireRole } from '@/routes/guards';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Callout, EmptyState, ErrorState, Skeleton } from '@/components/ui/feedback';
import { Pill } from '@/components/ui/primitives';

/**
 * The shadow-mode AI reviewer's report.
 *
 * A second, private opinion is formed on every manual-review case and stored without ever being
 * shown to the operator who decides it (`AiReviewOpinionSchema` in `@pf/contracts` — the read is
 * refused while a case is still undecided, precisely so an operator's decision is never anchored
 * by a machine's). This page is where that private opinion is finally compared against what the
 * operator actually did, after the fact.
 *
 * The whole design of this page follows from one worry: a shadow-review report is trivially easy
 * to misread as "the AI is right N% of the time", and every number here is one that would support
 * that misreading if it were shown carelessly. So:
 *
 * - There is **no overall agreement percentage** — only per-reason rows, because judging two Thai
 *   name spellings and judging whether a duplicate reference was already paid are not the same
 *   task, and averaging them hides both answers (`AiReviewAgreementResponseSchema`'s doc comment).
 * - Rows are grouped by **fairness tier** first, because whether agreement *means* anything at all
 *   depends on whether the deciding evidence was even in front of the reviewer.
 * - **Abstentions are their own column**, never folded into disagreement — on a tier where the
 *   right answer is "I cannot tell", abstaining is the reviewer behaving well.
 * - The page states plainly that **no human-vs-human baseline exists** — dual control here
 *   ratifies, it does not re-judge, so "agrees as often as two operators agree with each other" is
 *   not a sentence this data can support.
 *
 * The report itself is **SUPER-only**. Both endpoints it reads answer 403 to every other role
 * (`ai-review:inspect` is a SUPER-held permission), so `AiReviewPage` gates on the same role
 * before either query is ever created — an operator who is not SUPER never fires the request that
 * would 403, and never sees the report's shape, not even its loading skeleton.
 */

/** Quick-select windows, in hours. Defaults to a week: review cases resolve far slower than
 * deposits do, and a day is too thin a slice of the decided-case history to say anything. */
const WINDOWS = [
  { hours: 24, labelKey: 'aiReview.window.day' },
  { hours: 168, labelKey: 'aiReview.window.week' },
  { hours: 720, labelKey: 'aiReview.window.month' },
] as const;

const DEFAULT_RANGE: MetricsRange = { kind: 'preset', hours: 168 };

/** Fairness tiers, in the order the page argues them: most meaningful first. */
const FAIRNESS_TIERS: readonly ShadowFairness[] = ['SCORABLE', 'UNDERDETERMINED', 'NOT_DECIDABLE'];

/**
 * Verdict labels borrow the operator's own decision vocabulary verbatim
 * (`manualReview.propose.*`) rather than minting new copy — one concept, one name across the
 * console, so "Approve" on this page and "Approve" on the review screen are provably the same
 * word in every locale, not two translations that happen to agree today.
 */
const VERDICT_LABEL_KEYS: Readonly<Record<ShadowVerdict, string>> = {
  APPROVE: 'manualReview.propose.approveLabel',
  REJECT: 'manualReview.propose.rejectLabel',
  EXTERNAL: 'manualReview.propose.externalLabel',
  APPROVE_ADJUSTED: 'manualReview.propose.adjustedLabel',
  ABSTAIN: 'aiReview.verdict.abstain',
};

const VERDICT_TONE: Readonly<Record<ShadowVerdict, StateTone>> = {
  APPROVE: 'good',
  REJECT: 'bad',
  EXTERNAL: 'flight',
  APPROVE_ADJUSTED: 'good',
  ABSTAIN: 'idle',
};

/** The operator's own decision, in the same borrowed vocabulary — `REVERIFY` never actually
 * reaches this page (it is not a money decision and is excluded from the propose path), but the
 * map is total so a future contract addition is a compile error here rather than a blank cell. */
const HUMAN_DECISION_LABEL_KEYS: Readonly<Record<ReviewCaseDecision, string>> = {
  APPROVE: 'manualReview.propose.approveLabel',
  REJECT: 'manualReview.propose.rejectLabel',
  EXTERNAL: 'manualReview.propose.externalLabel',
  APPROVE_ADJUSTED: 'manualReview.propose.adjustedLabel',
  REVERIFY: 'manualReview.reverify.action',
};

const HUMAN_DECISION_TONE: Readonly<Record<ReviewCaseDecision, StateTone>> = {
  APPROVE: 'good',
  REJECT: 'bad',
  EXTERNAL: 'flight',
  APPROVE_ADJUSTED: 'good',
  REVERIFY: 'idle',
};

/** "24 hours" / "7 days" / "35 minutes" — same duration-speaking rule the Dashboard uses. */
function windowText(t: TFunction, hours: number): string {
  if (hours > 24 && hours % 24 === 0) return t('aiReview.window.days', { count: hours / 24 });
  if (hours < 1) return t('common.minutes', { count: Math.max(1, Math.round(hours * 60)) });
  return t('aiReview.window.hours', { count: Math.round(hours * 10) / 10 });
}

/**
 * `agreement_rate` as text — never `0%` for `null`. `null` means "nothing was compared" (every
 * opinion abstained, or none was collected at all), which is a different fact from "it disagreed
 * on everything", and the two must never render the same.
 */
function agreementRateText(t: TFunction, rate: number | null): string {
  return rate === null ? t('aiReview.table.notEnoughCompared') : `${rate.toFixed(1)}%`;
}

/** A confidence in `[0, 1]` as a whole percentage, or an em dash for "no answer to score". */
function confidenceText(value: number | null): string {
  return value === null ? '—' : `${String(Math.round(value * 100))}%`;
}

/**
 * A `NOT_DECIDABLE` row that agrees often and abstains rarely is a warning sign, not a strength —
 * the doc comment on `ShadowFairnessSchema` says agreement without the deciding evidence is
 * coincidence. Flagged rather than silently trusted.
 */
function isSuspiciouslyConfident(row: AiReviewAgreementRow): boolean {
  if (row.fairness !== 'NOT_DECIDABLE' || row.compared === 0 || row.agreement_rate === null) {
    return false;
  }
  return row.abstained / row.compared < 0.3 && row.agreement_rate >= 70;
}

function agreementColumns(t: TFunction): Column<AiReviewAgreementRow>[] {
  return [
    {
      id: 'reason',
      header: t('aiReview.table.reason'),
      priority: 'primary',
      cell: (row) => (
        <span className="inline-flex flex-wrap items-center gap-1.5">
          {t(REVIEW_REASON_META[row.reason]?.labelKey ?? row.reason)}
          {isSuspiciouslyConfident(row) ? (
            <Pill
              className={cn(TONE_CLASSES.attention.badge)}
              title={t('aiReview.table.suspiciousHint')}
            >
              <AlertTriangle className="size-3" aria-hidden="true" />
              {t('aiReview.table.suspiciousBadge')}
            </Pill>
          ) : null}
        </span>
      ),
    },
    {
      id: 'compared',
      header: t('aiReview.table.compared'),
      align: 'right',
      priority: 'secondary',
      cell: (row) => <span className="tnum">{row.compared}</span>,
    },
    {
      id: 'agreed',
      header: t('aiReview.table.agreed'),
      align: 'right',
      priority: 'secondary',
      cell: (row) => <span className="tnum">{row.agreed}</span>,
    },
    {
      id: 'disagreed',
      header: t('aiReview.table.disagreed'),
      align: 'right',
      priority: 'secondary',
      cell: (row) => <span className="tnum">{row.disagreed}</span>,
    },
    {
      id: 'abstained',
      header: t('aiReview.table.abstained'),
      align: 'right',
      priority: 'secondary',
      cell: (row) => <span className="tnum">{row.abstained}</span>,
    },
    {
      id: 'agreementRate',
      header: t('aiReview.table.agreementRate'),
      align: 'right',
      priority: 'secondary',
      cell: (row) => <span className="tnum">{agreementRateText(t, row.agreement_rate)}</span>,
    },
    {
      id: 'meanConfidence',
      header: t('aiReview.table.meanConfidence'),
      align: 'right',
      priority: 'detail',
      cell: (row) => <span className="tnum">{confidenceText(row.mean_confidence)}</span>,
    },
  ];
}

function disagreementColumns(t: TFunction): Column<AiReviewDisagreementRow>[] {
  return [
    {
      id: 'case',
      header: t('aiReview.disagreements.column.case'),
      priority: 'primary',
      cell: (row) => (
        <div className="space-y-1">
          <Link
            to="/ops/reviews/$caseId"
            params={{ caseId: row.case_id }}
            className="block font-mono text-sm text-foreground underline-offset-2 hover:underline"
          >
            {shortId(row.case_id)}
          </Link>
          <Link
            to="/ops/history/$transactionId"
            params={{ transactionId: row.transaction_id }}
            className="block font-mono text-xs text-muted-foreground underline-offset-2 hover:underline"
          >
            {shortId(row.transaction_id)}
          </Link>
          <span className="block text-2xs text-muted-foreground">
            {t(REVIEW_REASON_META[row.reason]?.labelKey ?? row.reason)}
          </span>
        </div>
      ),
    },
    {
      id: 'amount',
      header: t('aiReview.disagreements.column.amount'),
      align: 'right',
      priority: 'secondary',
      cell: (row) => <MoneyDisplay amount={row.amount} size="sm" />,
    },
    {
      id: 'human',
      header: t('aiReview.disagreements.column.human'),
      priority: 'secondary',
      className: 'max-w-xs',
      cell: (row) => (
        <div className="space-y-1">
          <Pill className={TONE_CLASSES[HUMAN_DECISION_TONE[row.human_decision]].badge}>
            {t(HUMAN_DECISION_LABEL_KEYS[row.human_decision])}
          </Pill>
          <p className="text-xs leading-relaxed text-muted-foreground">
            {row.human_note ?? t('aiReview.disagreements.noNote')}
          </p>
        </div>
      ),
    },
    {
      id: 'ai',
      header: t('aiReview.disagreements.column.ai'),
      priority: 'secondary',
      className: 'max-w-xs',
      cell: (row) => (
        <div className="space-y-1">
          <span className="inline-flex items-center gap-1.5">
            <Pill className={TONE_CLASSES[VERDICT_TONE[row.ai_verdict]].badge}>
              {t(VERDICT_LABEL_KEYS[row.ai_verdict])}
            </Pill>
            <span className="tnum text-2xs text-muted-foreground">
              {t('aiReview.disagreements.confidence', { value: confidenceText(row.ai_confidence) })}
            </span>
          </span>
          <p className="text-xs leading-relaxed text-muted-foreground">
            {row.ai_rationale ?? t('aiReview.disagreements.noRationale')}
          </p>
        </div>
      ),
    },
    {
      id: 'resolvedAt',
      header: t('aiReview.disagreements.column.resolvedAt'),
      priority: 'detail',
      cell: (row) => (
        <span className="tnum text-xs text-muted-foreground">
          {formatDateTimeCompact(row.resolved_at)}
        </span>
      ),
    },
  ];
}

/**
 * Which reviewer the report is about, when the window holds more than one.
 *
 * Absent entirely below two, which is the normal state: one reviewer answering means there is
 * nothing to choose between, and a select with a single option is furniture. It appears during a
 * head-to-head — repointing `AI_REVIEW_MODEL` re-enqueues the same cases under the new
 * `(case, model, prompt_version)` key, so both models answer the identical case set — and it is
 * the only way to read either one's rate, because the unfiltered view sums them.
 *
 * The options come from `reviewers`, which the API computes *without* its own model filter, so
 * selecting one does not collapse the menu to the one already selected.
 */
function ReviewerPicker({
  reviewers,
  selected,
  onSelect,
}: {
  reviewers: AiReviewAgreementResponse['reviewers'];
  selected: AiReviewReviewerSelection | null;
  onSelect: (next: AiReviewReviewerSelection | null) => void;
}): ReactNode {
  const { t } = useTranslation();
  if (reviewers.length < 2) return null;

  const keyOf = (model: string, promptVersion: string): string => `${model}|${promptVersion}`;
  const selectedKey = selected === null ? '' : keyOf(selected.model, selected.promptVersion);

  return (
    <label className="flex flex-wrap items-center gap-2 text-xs text-muted-foreground">
      {t('aiReview.reviewerPicker.label')}
      <select
        className="rounded-md border border-border/60 bg-background px-2 py-1 text-xs text-foreground"
        value={selectedKey}
        onChange={(event) => {
          const next = reviewers.find(
            (candidate) => keyOf(candidate.model, candidate.prompt_version) === event.target.value,
          );
          onSelect(next === undefined ? null : { model: next.model, promptVersion: next.prompt_version });
        }}
      >
        <option value="">{t('aiReview.reviewerPicker.all', { count: reviewers.length })}</option>
        {reviewers.map((candidate) => (
          <option
            key={keyOf(candidate.model, candidate.prompt_version)}
            value={keyOf(candidate.model, candidate.prompt_version)}
          >
            {t('aiReview.reviewerPicker.option', {
              model: candidate.model,
              version: candidate.prompt_version,
              answered: candidate.answered,
            })}
          </option>
        ))}
      </select>
    </label>
  );
}

/** Model + prompt version, prominent — or the mixed-versions warning when the API sent `null`. */
function ReviewerBadge({
  model,
  promptVersion,
}: {
  model: string | null;
  promptVersion: string | null;
}): ReactNode {
  const { t } = useTranslation();
  if (model === null || promptVersion === null) {
    return (
      <Callout tone="warning" title={t('aiReview.reviewerBadge.mixedTitle')}>
        {t('aiReview.reviewerBadge.mixedBody')}
      </Callout>
    );
  }
  return (
    <div className="flex flex-wrap items-center gap-2">
      <Pill className={TONE_CLASSES.flight.badge}>
        <Bot className="size-3" aria-hidden="true" />
        {t('aiReview.reviewerBadge.model', { model })}
      </Pill>
      <Pill>{t('aiReview.reviewerBadge.promptVersion', { version: promptVersion })}</Pill>
    </div>
  );
}

/** `failed`/`uncompared` — always on the page, never hidden behind a healthy-looking summary. */
function OperationalNote({ failed, uncompared }: { failed: number; uncompared: number }): ReactNode {
  const { t } = useTranslation();
  return (
    <dl className="grid grid-cols-2 gap-3 sm:max-w-sm">
      <div className="space-y-0.5 rounded-lg border border-border/60 px-3 py-2">
        <dt className="text-2xs text-muted-foreground">{t('aiReview.operational.failedLabel')}</dt>
        <dd className="tnum text-sm font-semibold text-foreground">{failed}</dd>
      </div>
      <div className="space-y-0.5 rounded-lg border border-border/60 px-3 py-2">
        <dt className="text-2xs text-muted-foreground">
          {t('aiReview.operational.uncomparedLabel')}
        </dt>
        <dd className="tnum text-sm font-semibold text-foreground">{uncompared}</dd>
      </div>
    </dl>
  );
}

/** One fairness tier's card: plain-language framing, then its rows. */
function FairnessTierCard({
  tier,
  rows,
}: {
  tier: ShadowFairness;
  rows: AiReviewAgreementRow[];
}): ReactNode {
  const { t } = useTranslation();
  return (
    <Card>
      <CardHeader>
        <CardTitle>{t(`aiReview.tiers.${tier}.title`)}</CardTitle>
        <CardDescription>{t(`aiReview.tiers.${tier}.description`)}</CardDescription>
      </CardHeader>
      <CardContent>
        <DataTable
          caption={t(`aiReview.tiers.${tier}.title`)}
          columns={agreementColumns(t)}
          rows={rows}
          rowKey={(row) => row.reason}
          empty={
            <EmptyState
              title={t('aiReview.tiers.empty.title')}
              description={t('aiReview.tiers.empty.description')}
            />
          }
        />
      </CardContent>
    </Card>
  );
}

/**
 * The disagreement list — the artefact rule 7 of the brief calls the point of the feature.
 *
 * Self-fetching, in the same shape `ArrivalScatterCard` uses on the Dashboard: its own query, its
 * own loading/error/empty state, so a failure here leaves the agreement report above it fully
 * readable rather than blanking the whole page.
 */
function DisagreementsSection({
  range,
  page,
  limit,
  onPageChange,
  onLimitChange,
}: {
  range: MetricsRange;
  page: number;
  limit: number;
  onPageChange: (page: number) => void;
  onLimitChange: (limit: number) => void;
}): ReactNode {
  const { t } = useTranslation();
  const disagreements = useAiReviewDisagreements({ range, page, limit });

  return (
    <Section
      title={t('aiReview.disagreements.title')}
      description={t('aiReview.disagreements.description')}
    >
      {disagreements.isLoading ? (
        <Skeleton className="h-64" />
      ) : disagreements.isError ? (
        <ErrorState
          error={disagreements.error}
          onRetry={() => {
            void disagreements.refetch();
          }}
        />
      ) : !disagreements.data ? null : (
        <div className="space-y-3">
          <DataTable
            caption={t('aiReview.disagreements.title')}
            columns={disagreementColumns(t)}
            rows={disagreements.data.items}
            rowKey={(row) => row.case_id}
            empty={
              <EmptyState
                title={t('aiReview.disagreements.empty.title')}
                description={t('aiReview.disagreements.empty.description')}
              />
            }
          />
          {disagreements.data.items.length > 0 ? (
            <Pager
              summary={t('aiReview.disagreements.pageSummary', {
                page: page + 1,
                pages: Math.max(1, Math.ceil(disagreements.data.total / limit)),
                total: disagreements.data.total,
              })}
              page={page}
              pages={Math.max(1, Math.ceil(disagreements.data.total / limit))}
              limit={limit}
              onPageChange={onPageChange}
              onLimitChange={onLimitChange}
            />
          ) : null}
        </div>
      )}
    </Section>
  );
}

function AiReviewSkeleton(): ReactNode {
  const { t } = useTranslation();
  return (
    <div className="space-y-8" role="status" aria-label={t('aiReview.skeleton.ariaLabel')}>
      <div className="space-y-2">
        <Skeleton className="h-7 w-64" />
        <Skeleton className="h-4 w-96 max-w-full" />
      </div>
      <Skeleton className="h-20" />
      <div className="grid gap-3">
        {Array.from({ length: 3 }, (_, i) => (
          <Skeleton key={i} className="h-48" />
        ))}
      </div>
      <Skeleton className="h-64" />
    </div>
  );
}

/**
 * The not-permitted state for a non-SUPER operator.
 *
 * A hidden nav link is not access control (a bookmark, or a typed URL, reaches this route just
 * the same), so this is the second, load-bearing half of the SUPER gate — not the red, envelope-
 * shaped {@link ErrorState} a 403 would otherwise produce, because nothing here went wrong: an
 * operator without the role is not expected to see this report at all.
 */
function AiReviewRestricted(): ReactNode {
  const { t } = useTranslation();
  const { session } = useAuth();
  return (
    <EmptyState
      icon={<ShieldAlert className="size-5" aria-hidden="true" />}
      title={t('aiReview.restricted.title')}
      description={t('aiReview.restricted.description', {
        currentRole: session?.area === 'operator' ? session.role : t('common.unknownRole'),
      })}
    />
  );
}

/**
 * The AI shadow-reviewer report — see the module doc comment above for what this page is careful
 * not to imply, and for why it is SUPER-only.
 */
export function AiReviewPage(): ReactNode {
  return (
    <RequireRole role="SUPER" fallback={<AiReviewRestricted />}>
      <AiReviewReport />
    </RequireRole>
  );
}

/** The report itself — only ever mounted for a SUPER session; see {@link AiReviewPage}. */
function AiReviewReport(): ReactNode {
  const { t } = useTranslation();
  const [range, setRange] = useState<MetricsRange>(DEFAULT_RANGE);
  const [page, setPage] = useState(0);
  const [limit, setLimit] = useState<number>(PAGE_SIZES[0]);
  /**
   * Which reviewer the rates below are about. `null` — no narrowing — is the honest default: it
   * is what the page did before a second reviewer was ever possible, and when the window really
   * does hold two, the API answers `model: null` and {@link ReviewerBadge} says so loudly rather
   * than the page quietly picking one on the operator's behalf.
   */
  const [reviewer, setReviewer] = useState<AiReviewReviewerSelection | null>(null);

  const agreement = useAiReviewAgreement(range, reviewer);

  if (agreement.isLoading) return <AiReviewSkeleton />;
  if (agreement.isError) {
    return (
      <>
        <PageHeader title={t('aiReview.title')} />
        <ErrorState
          error={agreement.error}
          onRetry={() => {
            void agreement.refetch();
          }}
        />
      </>
    );
  }
  if (!agreement.data) return null;

  const data = agreement.data;
  const settled = agreement.isPlaceholderData ? null : data;
  // Unlike the Dashboard's `/metrics`, this response carries no `window_hours` to echo back — the
  // requested bounds are what the operator asked for either way, so the preset branch names the
  // selection directly and only the custom branch prefers the settled server-reported bounds
  // (falling back to the just-typed ones while a new range is still in flight).
  const window =
    range.kind === 'custom'
      ? t('aiReview.window.range', {
          from: formatDateTimeCompact(settled?.range_start ?? range.from),
          to: formatDateTimeCompact(settled?.range_end ?? range.to),
        })
      : t('aiReview.window.last', { duration: windowText(t, range.hours) });

  return (
    <>
      <PageHeader
        title={t('aiReview.title')}
        description={t('aiReview.description', { window })}
        actions={
          <>
            <div
              role="group"
              aria-label={t('aiReview.window.label')}
              className="flex items-center gap-1.5"
            >
              {WINDOWS.map((candidate) => (
                <button
                  key={candidate.hours}
                  type="button"
                  aria-pressed={range.kind === 'preset' && range.hours === candidate.hours}
                  onClick={() => {
                    setRange({ kind: 'preset', hours: candidate.hours });
                    setPage(0);
                  }}
                  className={windowButtonClasses(
                    range.kind === 'preset' && range.hours === candidate.hours,
                  )}
                >
                  {t(candidate.labelKey)}
                </button>
              ))}
              <CustomRangePicker
                range={range}
                onApply={(next) => {
                  setRange(next);
                  setPage(0);
                }}
                onClear={() => {
                  setRange(DEFAULT_RANGE);
                  setPage(0);
                }}
              />
            </div>
            <Button
              variant="outline"
              size="sm"
              loading={agreement.isFetching}
              onClick={() => {
                void agreement.refetch();
              }}
            >
              <RefreshCw aria-hidden="true" />
              {t('aiReview.refresh')}
            </Button>
          </>
        }
      />

      {/*
       * Not buried: this is the single most important caveat on the page, so it sits right under
       * the header, before a single number is on screen. See the module doc comment for why the
       * data cannot support "agrees as often as two operators agree with each other".
       */}
      <Callout tone="info" title={t('aiReview.noBaseline.title')}>
        {t('aiReview.noBaseline.body')}
      </Callout>

      <Section
        title={t('aiReview.agreement.title')}
        description={t('aiReview.agreement.description')}
      >
        <ReviewerPicker
          reviewers={data.reviewers}
          selected={reviewer}
          onSelect={setReviewer}
        />
        <ReviewerBadge model={data.model} promptVersion={data.prompt_version} />
        <OperationalNote failed={data.failed} uncompared={data.uncompared} />
        <div className="grid gap-3">
          {FAIRNESS_TIERS.map((tier) => (
            <FairnessTierCard
              key={tier}
              tier={tier}
              rows={data.rows.filter((row) => row.fairness === tier)}
            />
          ))}
        </div>
      </Section>

      <DisagreementsSection
        range={range}
        page={page}
        limit={limit}
        onPageChange={setPage}
        onLimitChange={(next) => {
          setLimit(next);
          setPage(0);
        }}
      />

      <p className="text-2xs text-muted-foreground">
        {t('aiReview.footer', { when: formatRelative(data.generated_at) })}
      </p>
    </>
  );
}
